/**
 * PostingLayout — Task 8
 * Full posting page. Mirrors FOM posting.html.
 * Accessible at /posting. Auth-gated — redirects to /login if no session.
 *
 * NEXT SHIFT DRAFT — parity with latest FOM posting.html:
 *   - Tab bar above the form: Current Shift | Next Shift (hidden until window).
 *   - "Next Shift" button shows within 30 min of the current shift end, or
 *     whenever a draft already exists, or while the user is on the next tab.
 *   - Banner on the next tab explains the draft semantics and exposes a
 *     "Promote to Current" button that replaces the current posting.
 *   - A 60-second interval keeps the tab visibility in sync.
 */
import { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore, isExecRole } from '../stores/auth'
import { usePostingStore, nextShiftLabel } from '../stores/posting'
import { useSyncStore, syncColor, markDirty } from '../stores/sync'
import PersonCard from '../components/posting/PersonCard'

interface PostingLayoutProps {
  /** When true, hides the standalone topbar and the 100vh background.
   *  Used when rendered inside DesktopShell's PostingModal. */
  embedded?: boolean
}

export default function PostingLayout({ embedded = false }: PostingLayoutProps = {}) {
  const session = useAuthStore(s => s.session)
  const canEdit = isExecRole(session?.rank ?? '')
  const { data, nextData, activeTab, nextTabVisible,
    setDate, setShift, setNotes, addSupervisor, addOfficer, hydrateFromStorage,
    switchTab, promoteNextShift, checkNextShiftWindow,
  } = usePostingStore()
  const syncStatus = useSyncStore(s => s.status)
  const { pull, push } = useSyncStore()
  const navigate = useNavigate()

  // Active data for the render — swap between current and next draft
  const active = activeTab === 'next' ? nextData : data

  // Mobile detection — when accessed standalone (not embedded in desktop modal)
  // on a phone, we want a modal-style top bar with a clear close button.
  const isMobile = !embedded && typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)

  function closeAndReturnToMenu() {
    // Push any pending edits before leaving so the menu/MCV reflects them
    try { push() } catch { /* silent */ }
    navigate('/mobile')
  }

  if (!session) return <Navigate to="/login" replace />

  // Re-hydrate after sync pull
  useEffect(() => {
    if (syncStatus === 'ok') hydrateFromStorage()
  }, [syncStatus])

  // Check next-shift tab visibility immediately and every 60s thereafter.
  // Mirrors FOM posting.html's setInterval(checkNextShiftTab, 60000).
  useEffect(() => {
    checkNextShiftWindow()
    const id = window.setInterval(checkNextShiftWindow, 60_000)
    return () => window.clearInterval(id)
  }, [data.shift, data.date])

  const dotColor = syncColor(syncStatus)

  const inp: React.CSSProperties = {
    background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-primary)', fontSize: 13, padding: '7px 10px', outline: 'none',
  }
  const section: React.CSSProperties = {
    background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14,
    marginBottom: 14, overflow: 'hidden',
  }
  const sectionHdr = (icon: string, title: string) => (
    <div style={{ background: 'linear-gradient(135deg,var(--bg-elevated),var(--bg-surface))', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>{title}</span>
    </div>
  )

  // Tab button styles
  const tabBtn = (isActive: boolean): React.CSSProperties => ({
    padding: '9px 20px',
    border: 'none',
    background: 'transparent',
    color: isActive ? '#c9a84c' : '#64748b',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    borderBottom: isActive ? '3px solid #c9a84c' : '3px solid transparent',
    marginBottom: -2,
    fontFamily: 'inherit',
    transition: 'all .15s',
  })

  const hasDraftEntries = nextData.supervisors.some(s => s.cs) || nextData.officers.some(o => o.cs)

  function handlePromote() {
    if (window.confirm('Promote Next Shift draft to current? This will replace the current posting.')) {
      promoteNextShift()
    }
  }

  return (
    <div style={{ background: 'var(--bg-deep)', color: 'var(--text-primary)', fontFamily: "'Segoe UI', system-ui, sans-serif", ...(isMobile ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' } : { minHeight: embedded ? 0 : '100vh' }) }}>

      {/* Mobile modal-style top bar — FIXED, two rows: title bar + action toolbar */}
      {isMobile && (
        <div style={{
          background: 'linear-gradient(135deg,var(--bg-elevated),var(--bg-surface))',
          borderBottom: '2px solid var(--accent)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
          paddingTop: 'env(safe-area-inset-top)',
        }}>
          {/* Row 1 — close, title, sync status */}
          <div style={{
            padding: '10px 14px 6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}>
            <button
              onClick={closeAndReturnToMenu}
              style={{
                background: 'rgba(239,68,68,0.15)',
                border: '1px solid rgba(239,68,68,0.45)',
                borderRadius: 8,
                color: '#fca5a5',
                fontSize: 13,
                fontWeight: 700,
                padding: '7px 14px',
                cursor: 'pointer',
                minWidth: 72,
              }}
            >✕ Close</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center' }}>
              <span style={{ fontSize: 16 }}>📋</span>
              <span style={{ color: '#c9a84c', fontWeight: 800, fontSize: 14, letterSpacing: 0.5 }}>Daily Posting</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 72, justifyContent: 'flex-end' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{syncStatus === 'ok' ? 'Live' : '...'}</span>
            </div>
          </div>

          {/* Row 2 — pull / push / print action toolbar */}
          <div style={{
            padding: '0 10px 10px',
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
          }}>
            <button
              onClick={() => pull()}
              style={{
                flex: 1,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                color: 'var(--text-muted)',
                fontSize: 12,
                fontWeight: 700,
                padding: '8px 6px',
                cursor: 'pointer',
              }}
            >⬇ Pull</button>
            <button
              onClick={() => push()}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg,#c9a84c,#dbb960)',
                border: 'none',
                borderRadius: 8,
                color: '#1a2744',
                fontSize: 12,
                fontWeight: 800,
                padding: '8px 6px',
                cursor: 'pointer',
              }}
            >⬆ Push</button>
            <button
              onClick={() => window.print()}
              style={{
                flex: 1,
                background: '#7c3aed',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                padding: '8px 6px',
                cursor: 'pointer',
              }}
            >🖨 Print</button>
          </div>
        </div>
      )}

      {/* Desktop topbar — hidden when embedded or on mobile */}
      {!embedded && !isMobile && <div style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="20" height="20" viewBox="0 0 32 32">
            <defs><linearGradient id="pg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#c9a84c"/><stop offset="100%" stopColor="#dbb960"/></linearGradient></defs>
            <path d="M16 2L28 7L28 18C28 25 22 30 16 32C10 30 4 25 4 18L4 7Z" fill="url(#pg)"/>
            <path d="M16 6L24 10L24 18C24 23 20 27 16 29C12 27 8 23 8 18L8 10Z" fill="#162033"/>
          </svg>
          <span style={{ color: '#c9a84c', fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>CASTELLAN</span>
          <span style={{ color: 'var(--text-hint)', fontSize: 11 }}>Daily Posting</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor }} />
          <button onClick={() => pull()} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)', fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>⬇ Pull</button>
          <button onClick={() => push()} style={{ background: 'linear-gradient(135deg,#c9a84c,#dbb960)', border: 'none', borderRadius: 6, color: '#1a2744', fontSize: 11, fontWeight: 700, padding: '4px 10px', cursor: 'pointer' }}>⬆ Push</button>
          <button onClick={() => window.print()} style={{ background: '#7c3aed', border: 'none', borderRadius: 6, color: '#fff', fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>🖨 Print</button>
        </div>
      </div>}

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '16px 14px', ...(isMobile ? { position: 'fixed', top: 'calc(112px + env(safe-area-inset-top))', left: 0, right: 0, bottom: 72, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : {}) }}>

        {/* Tab bar — Current Shift / Next Shift */}
        <div style={{
          display: 'flex',
          gap: 0,
          marginBottom: 16,
          borderBottom: '2px solid var(--border)',
        }}>
          <button style={tabBtn(activeTab === 'current')} onClick={() => switchTab('current')}>
            📋 Current Shift
          </button>
          {nextTabVisible && (
            <button style={tabBtn(activeTab === 'next')} onClick={() => switchTab('next')}>
              ⏭️ Next Shift
              {hasDraftEntries && (
                <span style={{
                  display: 'inline-block',
                  background: '#059669',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 800,
                  borderRadius: 10,
                  padding: '1px 6px',
                  marginLeft: 6,
                  verticalAlign: 'middle',
                }}>DRAFT</span>
              )}
            </button>
          )}
        </div>

        {/* Banner when editing the next shift draft */}
        {activeTab === 'next' && (
          <div style={{
            background: 'rgba(245,158,11,0.1)',
            border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 14,
            fontSize: 12,
            color: '#fbbf24',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 10,
          }}>
            <span>
              ⏭️ Editing <strong>Next Shift: {nextShiftLabel(data.shift || 'Morning')} Shift</strong> — saves as a draft and
              does not affect the current shift.
            </span>
            <button onClick={handlePromote} style={{
              padding: '4px 10px',
              background: '#c9a84c',
              color: '#0f1a2e',
              border: 'none',
              borderRadius: 6,
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 11,
            }}>▲ Promote to Current</button>
          </div>
        )}

        {/* Date + Shift selector */}
        {canEdit && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>Date</label>
              <input
                type="date"
                value={active.date}
                onChange={e => { markDirty(); setDate(e.target.value) }}
                style={{ ...inp, width: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>Shift</label>
              <select
                value={active.shift}
                onChange={e => { markDirty(); setShift(e.target.value) }}
                style={{ ...inp, cursor: 'pointer' }}
              >
                <option value="Morning">Morning (0700–1500)</option>
                <option value="Evening">Evening (1500–2300)</option>
                <option value="Night">Night (2300–0700)</option>
              </select>
            </div>
          </div>
        )}

        {/* Supervisors */}
        <div style={section}>
          {sectionHdr('⭐', 'Supervisors')}
          <div style={{ padding: 14 }}>
            {active.supervisors.map((s, i) => (
              <PersonCard key={i} entry={s} index={i} type="sup" shift={active.shift} />
            ))}
            <button onClick={addSupervisor}
              style={{ width: '100%', padding: '10px', background: 'rgba(201,168,76,0.1)', border: '1px dashed rgba(201,168,76,0.4)', borderRadius: 8, color: '#c9a84c', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
              + Add Supervisor
            </button>
          </div>
        </div>

        {/* Officers */}
        <div style={section}>
          {sectionHdr('👮', 'Officers')}
          <div style={{ padding: 14 }}>
            {active.officers.map((o, i) => (
              <PersonCard key={i} entry={o} index={i} type="ofc" shift={active.shift} />
            ))}
            <button onClick={addOfficer}
              style={{ width: '100%', padding: '10px', background: 'rgba(96,165,250,0.1)', border: '1px dashed rgba(96,165,250,0.4)', borderRadius: 8, color: '#60a5fa', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
              + Add Officer
            </button>
          </div>
        </div>

        {/* Notes */}
        <div style={section}>
          {sectionHdr('📝', 'Notes / Special Details')}
          <div style={{ padding: 14 }}>
            {canEdit ? (
              <textarea
                value={active.notes}
                onChange={e => { markDirty(); setNotes(e.target.value) }}
                placeholder="Enter shift notes, special instructions, or announcements…"
                style={{ ...inp, width: '100%', boxSizing: 'border-box', minHeight: 100, resize: 'vertical', lineHeight: 1.7 }}
              />
            ) : (() => {
              const lines = (active.notes || '').split('\n').map(l => l.replace(/^[•·\-*]\s*\t?/, '').trim()).filter(Boolean)
              if (lines.length === 0) {
                return <div style={{ color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic', textAlign: 'center', padding: '14px 4px' }}>No notes for this shift.</div>
              }
              return (
                <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.7, listStyleType: 'disc' }}>
                  {lines.map((n, i) => <li key={i} style={{ marginBottom: 4 }}>{n}</li>)}
                </ul>
              )
            })()}
          </div>
        </div>

      </div>

      {/* Mobile bottom bar — FIXED */}
      {isMobile && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(15,26,46,0.98)',
          backdropFilter: 'blur(16px)',
          borderTop: '1px solid var(--border)',
          padding: '12px 14px',
          display: 'flex',
          gap: 10,
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 100,
        }}>
          <button
            onClick={closeAndReturnToMenu}
            style={{
              flex: 1,
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.4)',
              borderRadius: 10,
              color: '#fca5a5',
              fontSize: 13,
              fontWeight: 700,
              padding: '12px 16px',
              cursor: 'pointer',
            }}
          >Cancel</button>
          <button
            onClick={closeAndReturnToMenu}
            style={{
              flex: 2,
              background: 'linear-gradient(135deg,#c9a84c,#dbb960)',
              border: 'none',
              borderRadius: 10,
              color: '#1a2744',
              fontSize: 14,
              fontWeight: 800,
              padding: '12px 16px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(201,168,76,0.3)',
            }}
          >✓ Done</button>
        </div>
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
    </div>
  )
}
